import React, { useMemo, useState } from "react";
import { Button, Col, Divider, Row, Space, Table, Tag, Typography } from "antd";
import { medicineFrequency } from "../../../helpers/medicineKeys";
import StatMedicineModal from "./StatMedicineModal";
import buttonStyle from "../../../styles/Custom/Button.module.css";
import dayjs from "dayjs";
import { ClockCircleOutlined } from "@ant-design/icons";
import ExpandableRemarks from "../../IpEMR/Nurse/CareItems/ExpandableRemarks";
import useIsMobile from "../../../hooks/useIsMobile";

const StatMedicines = ({ statData, refreshPage, variant = "default" }) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const TILE_W = 170;
  const TILE_GAP = 12;

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setOpen(false);
  };

  const formatDateTime = (iso) => {
    if (!iso) return null;
    const d = dayjs(iso);
    return d.isValid() ? d.format("hh:mm A") : null;
  };

  const renderNurseTimingEntries = (record) => {
    const actions = record?.actions || [];
    const entries = actions
      .flatMap((a) => (Array.isArray(a?.nurse_data) ? a.nurse_data : []))
      .filter(Boolean)
      .sort((a, b) => dayjs(a?.process_time).valueOf() - dayjs(b?.process_time).valueOf());

    if (entries.length === 0) return null;

    return (
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "nowrap",
          justifyContent: "flex-start",
          width: "100%",
        }}
      >
        {entries.map((e, idx) => (
          <div
            key={`${e?.process_time ?? "t"}-${idx}`}
            style={{
              width: 170,
              minWidth: 170,
              maxWidth: 170,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 4,
            }}
          >
            <Typography style={{ fontWeight: 600, fontSize: 12, color: "#111827" }}>
              {formatDateTime(e?.process_time) ?? "-"}
            </Typography>
            <ExpandableRemarks
              text={e?.remarks}
              rows={2}
              style={{
                fontSize: 11,
                color: "#667085",
                width: "100%",
                textAlign: "left",
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  const drugData = (item) => {
    return (
      <Space style={{ marginBottom: "8px" }}>
        {item?.drug && (
          <Typography style={{ fontSize: "10px" }}>Drug : {item?.drug}</Typography>
        )}
        {item?.dose && (
          <Typography style={{ fontSize: "10px" }}>Dose: {item?.dose}</Typography>
        )}
        {item?.route && (
          <Typography style={{ fontSize: 10 }}>Route: {item?.route}</Typography>
        )}
        {item?.days_left_adjusted && (
          <Typography style={{ fontSize: 10 }}>
            Duration: {item?.days_left_adjusted} days
          </Typography>
        )}
      </Space>
    );
  };

  const maxNurseTimings = useMemo(() => {
    const rows = Array.isArray(statData) ? statData : [];
    return rows.reduce((max, med) => {
      const count = (med?.actions || []).reduce((acc, a) => {
        if (Array.isArray(a?.nurse_data)) return acc + a.nurse_data.length;
        return acc;
      }, 0);
      return Math.max(max, count);
    }, 0);
  }, [statData]);

  const timingsColWidth = useMemo(() => {
    if (!maxNurseTimings) return 520;
    const contentW =
      maxNurseTimings * TILE_W + Math.max(0, maxNurseTimings - 1) * TILE_GAP;
    return Math.max(520, contentW + 24);
  }, [maxNurseTimings]);

  const statSummaryColumns = useMemo(
    () => [
      {
        title: "Drug & Dosage",
        key: "drug_dose",
        width: 340,
        ...(isMobile ? {} : { fixed: "left" }),
        render: (_, record) => (
          <div style={{ minWidth: 260 }}>
            <Typography style={{ fontWeight: 500, fontSize: 12 }}>
              {record?.name ?? "-"}
              {record?.dose ? ` (${record?.dose})` : ""}
            </Typography>
            <Typography style={{ fontSize: 10, color: "#667085" }}>
              {record?.drug ? `Drug: ${record?.drug}` : ""}
              {record?.route ? `  Route: ${record?.route}` : ""}
              {record?.days_left_adjusted
                ? `  Duration: ${record?.days_left_adjusted} days`
                : ""}
            </Typography>
          </div>
        ),
      },
      {
        title: "Doctor's Comment",
        dataIndex: "comments",
        key: "comments",
        width: 200,
        ...(isMobile ? {} : { fixed: "left" }),
        align: "center",
        render: (v) => v ?? "-",
      },
      {
        title: "Timings",
        key: "timings",
        width: timingsColWidth,
        align: "left",
        onCell: () => ({ style: { overflow: "hidden" } }),
        render: (_, record) =>
          renderNurseTimingEntries(record) ?? (
            <div className="nurse-med-timings-stat-text">-</div>
          ),
      },
    ],
    [timingsColWidth, isMobile]
  );

  const isStatMedicineStopped = (item) => item?.actions?.[0]?.is_stopped === true;

  const getFreqLabel = (item) =>
    medicineFrequency?.find((med) => med?.value === item?.frequency)?.label ?? "";

  const renderMobileStatChip = (item, index) => {
    const freqLabel = getFreqLabel(item);
    const stopped = isStatMedicineStopped(item);
    const isStat = item?.frequency === "Immediately";
    const isSos = item?.frequency === "If_Required";
    const processed = item?.actions?.[0]?.processed === true;
    const chipKey = item?.id ?? `${item?.name}-${index}`;

    const chipClass = [
      "nurse-stat-med-chip",
      stopped && "nurse-stat-med-chip--stopped",
      !stopped && isStat && !processed && "nurse-stat-med-chip--stat",
      !stopped && isSos && "nurse-stat-med-chip--sos",
      processed && "nurse-stat-med-chip--done",
    ]
      .filter(Boolean)
      .join(" ");

    if (stopped) {
      return (
        <div key={chipKey} className={chipClass}>
          <span className="nurse-stat-med-chip__name">{item?.name}</span>
          <div className="nurse-stat-med-chip__meta">
            <span className="nurse-stat-med-chip__badge">{freqLabel}</span>
            <span className="nurse-stat-med-chip__stopped">Stopped</span>
          </div>
        </div>
      );
    }

    return (
      <button
        key={chipKey}
        type="button"
        className={chipClass}
        disabled={isStat && processed}
        onClick={() => handleOpenModal(item)}
      >
        <span className="nurse-stat-med-chip__name">{item?.name}</span>
        <div className="nurse-stat-med-chip__meta">
          <span className="nurse-stat-med-chip__badge">{freqLabel}</span>
        </div>
      </button>
    );
  };

  if (variant === "inline") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span className="nurse-med-table-heading">Stat / SOS Medicines</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {statData.map((item, index) => (
            <React.Fragment key={item?.id ?? `${item?.name}-${index}`}>
              {isStatMedicineStopped(item) ? (
                <span style={{ fontWeight: 400, fontSize: 12 }}>
                  {item?.name} -{" "}
                  {
                    medicineFrequency?.find((med) => med?.value === item?.frequency)
                      ?.label
                  }{" "}
                  <Tag color="red" style={{ marginLeft: 4 }}>
                    Stopped
                  </Tag>
                </span>
              ) : (
              <Button
                type="primary"
                ghost
                style={{
                  borderColor:
                    item?.frequency === "Immediately" &&
                    item?.actions?.[0]?.processed !== true
                      ? "#FED7D7"
                      : undefined,
                  color:
                    item?.frequency === "Immediately" &&
                    item?.actions?.[0]?.processed !== true
                      ? "#C64141"
                      : undefined,
                  fontWeight: 400,
                  fontSize: 12,
                  height: 30,
                }}
                className={`${buttonStyle.mdBtn} inv-btn`}
                disabled={
                  item?.frequency === "Immediately" &&
                  item?.actions?.[0]?.processed === true
                }
                onClick={() => handleOpenModal(item)}
              >
                {item?.name} -{" "}
                {
                  medicineFrequency?.find((med) => med?.value === item?.frequency)
                    ?.label
                }
              </Button>
              )}

              {open &&
                selectedItem?.name === item?.name &&
                !isStatMedicineStopped(item) && (
                <StatMedicineModal
                  open={open}
                  setOpen={handleCloseModal}
                  frequency={selectedItem?.frequency}
                  actions={selectedItem?.actions}
                  refreshPage={refreshPage}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "header") {
    return (
      <div style={{ width: "100%", maxWidth: "100%" }}>
        <div className="nurse-med-table-heading">Stat / SOS Medicines</div>
        <Row gutter={[16, 8]} justify="start">
          {statData.map((item, index) => (
            <Col key={item?.id ?? `${item?.name}-${index}`}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Button
                  type="primary"
                  ghost
                  style={{
                    borderColor:
                      item?.frequency === "Immediately" &&
                      item?.actions?.[0]?.processed !== true
                        ? "#FED7D7"
                        : undefined,
                    color:
                      item?.frequency === "Immediately" &&
                      item?.actions?.[0]?.processed !== true
                        ? "#C64141"
                        : undefined,
                    fontWeight: "lighter",
                    fontSize: "12px",
                    marginBottom: "6px",
                    height: 30,
                  }}
                  className={`${buttonStyle.mdBtn} inv-btn`}
                  disabled={
                    item?.frequency === "Immediately" &&
                    item?.actions?.[0]?.processed === true
                  }
                  onClick={() => handleOpenModal(item)}
                >
                  {item?.name} -{" "}
                  {
                    medicineFrequency?.find((med) => med?.value === item?.frequency)
                      ?.label
                  }
                </Button>

                {open && selectedItem?.name === item?.name && (
                  <StatMedicineModal
                    open={open}
                    setOpen={handleCloseModal}
                    frequency={selectedItem?.frequency}
                    actions={selectedItem?.actions}
                    refreshPage={refreshPage}
                  />
                )}
              </div>
            </Col>
          ))}
        </Row>

        <div style={{ marginTop: 8 }}>
          <Table
            size="small"
            bordered
            pagination={false}
            dataSource={statData}
            columns={statSummaryColumns}
            rowKey={(r, idx) => r?.id ?? `${r?.name}-${idx}`}
            className="stat-sos-summary-table"
            style={{ width: "100%" }}
            tableLayout="fixed"
            scroll={{ x: 340 + 200 + timingsColWidth }}
          />
        </div>
      </div>
    );
  }

  if (variant === "headerButtons") {
    if (isMobile) {
      return (
        <div className="nurse-stat-med-section">
          <div className="nurse-med-table-heading">Stat / SOS Medicines</div>
          <div className="nurse-stat-med-chips">
            {statData.map((item, index) => renderMobileStatChip(item, index))}
          </div>
          {open && selectedItem && !isStatMedicineStopped(selectedItem) && (
            <StatMedicineModal
              open={open}
              setOpen={handleCloseModal}
              frequency={selectedItem?.frequency}
              actions={selectedItem?.actions}
              refreshPage={refreshPage}
            />
          )}
        </div>
      );
    }

    return (
      <div style={{ width: "100%", maxWidth: "100%" }}>
        <div className="nurse-med-table-heading">Stat / SOS Medicines</div>
        <Row
          gutter={isMobile ? [8, 6] : [16, 8]}
          justify="start"
          className="nurse-stat-med-buttons"
        >
          {statData.map((item, index) => (
            <Col
              key={item?.id ?? `${item?.name}-${index}`}
              className="nurse-stat-med-buttons-col"
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                {isStatMedicineStopped(item) ? (
                  <span
                    style={{
                      fontWeight: "lighter",
                      fontSize: "12px",
                      marginBottom: "6px",
                    }}
                  >
                    {item?.name} -{" "}
                    {
                      medicineFrequency?.find(
                        (med) => med?.value === item?.frequency
                      )?.label
                    }{" "}
                    <Tag color="red" style={{ marginLeft: 4 }}>
                      Stopped
                    </Tag>
                  </span>
                ) : (
                  <Button
                    type="primary"
                    ghost
                    style={{
                      borderColor:
                        item?.frequency === "Immediately" &&
                        item?.actions?.[0]?.processed !== true
                          ? "#FED7D7"
                          : undefined,
                      color:
                        item?.frequency === "Immediately" &&
                        item?.actions?.[0]?.processed !== true
                          ? "#C64141"
                          : undefined,
                      fontWeight: "lighter",
                      fontSize: isMobile ? "11px" : "12px",
                      marginBottom: isMobile ? 0 : "6px",
                      height: isMobile ? 28 : 30,
                      padding: isMobile ? "0 8px" : undefined,
                    }}
                    className={`${buttonStyle.mdBtn} inv-btn`}
                    disabled={
                      item?.frequency === "Immediately" &&
                      item?.actions?.[0]?.processed === true
                    }
                    onClick={() => handleOpenModal(item)}
                  >
                    {item?.name} -{" "}
                    {
                      medicineFrequency?.find(
                        (med) => med?.value === item?.frequency
                      )?.label
                    }
                  </Button>
                )}

                {open &&
                  selectedItem?.name === item?.name &&
                  !isStatMedicineStopped(item) && (
                  <StatMedicineModal
                    open={open}
                    setOpen={handleCloseModal}
                    frequency={selectedItem?.frequency}
                    actions={selectedItem?.actions}
                    refreshPage={refreshPage}
                  />
                )}
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <>
      <span>Stat / SOS Medicines</span>
      <br />
      <Row
        gutter={[24, 0]}
        justify="start"
        style={{
          padding: "10px 50px",
        }}
      >
        {statData.map((item, index) => (
          <>
            <Col span={7}>
              <Row>
                {(item?.frequency === "Immediately" &&
                  item?.actions?.[0]?.processed === true) ||
                item?.actions?.[0]?.is_stopped ? (
                  <span style={{ fontWeight: "lighter", fontSize: "12px" }}>
                    {item?.name} -{" "}
                    {
                      medicineFrequency?.find(
                        (med) => med?.value === item?.frequency
                      )?.label
                    }
                  </span>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Button
                      type="primary"
                      ghost
                      style={{
                        borderColor:
                          item?.frequency === "Immediately" &&
                          item?.actions?.[0]?.processed !== true &&
                          "#FED7D7",
                        color:
                          item?.frequency === "Immediately" &&
                          item?.actions?.[0]?.processed !== true &&
                          "#C64141",
                        fontWeight: "lighter",
                        fontSize: "12px",
                        marginBottom: "8px",
                      }}
                      className={`${buttonStyle.mdBtn} inv-btn`}
                      disabled={
                        item?.frequency === "Immediately" &&
                        item?.actions?.[0]?.processed === true
                      }
                      onClick={() => handleOpenModal(item)}
                    >
                      {item?.name} -{" "}
                      {
                        medicineFrequency?.find(
                          (med) => med?.value === item?.frequency
                        )?.label
                      }
                    </Button>
                    {drugData(item)}
                  </div>
                )}
                {item?.actions?.[0]?.is_stopped && (
                  <Tag
                    color="red"
                    style={{
                      height: "20px",
                      marginLeft: "8px",
                    }}
                  >
                    Stopped
                  </Tag>
                )}
              </Row>

              {item?.frequency === "Immediately" &&
                item?.actions?.[0]?.processed === true && (
                  <Row gutter={[8, 8]} style={{ marginTop: "2px" }}>
                    <Col>
                      <Tag
                        color={
                          item?.frequency === "Immediately" &&
                          item?.actions?.[0]?.processed === true
                            ? "gray"
                            : "orange"
                        }
                        style={{ padding: "0px 3px", fontSize: "9px" }}
                      >
                        <Space size={3}>
                          <ClockCircleOutlined />
                          {item?.actions?.[0]?.process_time &&
                            dayjs(
                              item?.actions?.[0]?.nurse_data?.[0]?.process_time
                            )?.format("hh:mm A")}
                        </Space>
                      </Tag>
                    </Col>

                    <Col>
                      <ExpandableRemarks
                        text={item?.actions?.[0]?.nurse_data?.[0]?.remarks}
                        style={{ width: 300, fontSize: 12, color: "#000" }}
                      />
                    </Col>
                    {drugData(item)}
                  </Row>
                )}

              {item?.frequency === "If_Required" &&
                item?.actions?.[0]?.processed === true &&
                item?.actions?.[0]?.nurse_data?.map((nurseItem, nurseIndex) => (
                  <Row
                    gutter={[8]}
                    style={{ marginTop: "5px" }}
                    key={nurseIndex}
                  >
                    <Col>
                      <Tag
                        color="purple"
                        style={{ padding: "0px 3px", fontSize: "9px" }}
                      >
                        <Space size={3}>
                          <ClockCircleOutlined />
                          {dayjs(nurseItem?.process_time)?.format("hh:mm A")}
                        </Space>
                      </Tag>
                    </Col>

                    <Col>
                      <ExpandableRemarks
                        text={nurseItem?.remarks}
                        style={{ width: 300, fontSize: 12, color: "#000" }}
                      />
                    </Col>
                  </Row>
                ))}

              {open && selectedItem?.name === item?.name && (
                <StatMedicineModal
                  open={open}
                  setOpen={handleCloseModal}
                  frequency={selectedItem?.frequency}
                  actions={selectedItem?.actions}
                  refreshPage={refreshPage}
                />
              )}
            </Col>{" "}
            {(index + 1) % 3 !== 0 && index < statData.length - 1 && (
              <Col>
                <Divider
                  style={{ borderColor: "#D0D5DD", height: "50px" }}
                  type="vertical"
                />
              </Col>
            )}
          </>
        ))}
      </Row>
    </>
  );
};

export const StatSosSummaryTable = ({ statData = [], timingsRegionWidth: timingsRegionWidthProp }) => {
  const isMobile = useIsMobile();
  const TILE_W = 170;
  const TILE_GAP = 12;
  const formatDateTime = (iso) => {
    if (!iso) return null;
    const d = dayjs(iso);
    return d.isValid() ? d.format("hh:mm A") : null;
  };

  const renderNurseTimingEntries = (record) => {
    const actions = record?.actions || [];
    const entries = actions
      .flatMap((a) => (Array.isArray(a?.nurse_data) ? a.nurse_data : []))
      .filter(Boolean)
      .sort(
        (a, b) => dayjs(a?.process_time).valueOf() - dayjs(b?.process_time).valueOf()
      );

    if (entries.length === 0) return null;

    return (
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "nowrap",
          justifyContent: "flex-start",
          width: "100%",
        }}
      >
        {entries.map((e, idx) => (
          <div
            key={`${e?.process_time ?? "t"}-${idx}`}
            style={{
              width: 170,
              minWidth: 170,
              maxWidth: 170,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 4,
            }}
          >
            <Typography style={{ fontWeight: 600, fontSize: 12, color: "#111827" }}>
              {formatDateTime(e?.process_time) ?? "-"}
            </Typography>
            <ExpandableRemarks
              text={e?.remarks}
              rows={2}
              style={{
                fontSize: 11,
                color: "#667085",
                width: "100%",
                textAlign: "left",
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  const maxNurseTimings = (Array.isArray(statData) ? statData : []).reduce(
    (max, med) => {
      const count = (med?.actions || []).reduce((acc, a) => {
        if (Array.isArray(a?.nurse_data)) return acc + a.nurse_data.length;
        return acc;
      }, 0);
      return Math.max(max, count);
    },
    0
  );

  const computedTimingsColWidth = (() => {
    if (!maxNurseTimings) return 520;
    const contentW =
      maxNurseTimings * TILE_W + Math.max(0, maxNurseTimings - 1) * TILE_GAP;
    return Math.max(520, contentW + 24);
  })();

  const timingsColWidth =
    typeof timingsRegionWidthProp === "number" && timingsRegionWidthProp > 0
      ? timingsRegionWidthProp
      : computedTimingsColWidth;

  const columns = [
    {
      title: "Drug & Dosage",
      key: "drug_dose",
      width: 340,
      ...(isMobile ? {} : { fixed: "left" }),
      render: (_, record) => (
        <div style={{ minWidth: isMobile ? 200 : 260 }}>
          <Typography style={{ fontWeight: 500, fontSize: 12 }}>
            {record?.name ?? "-"}
            {record?.dose ? ` (${record?.dose})` : ""}
          </Typography>
          <Typography style={{ fontSize: 10, color: "#667085" }}>
            {record?.drug ? `Drug: ${record?.drug}` : ""}
            {record?.route ? `  Route: ${record?.route}` : ""}
            {record?.days_left_adjusted ? `  Duration: ${record?.days_left_adjusted} days` : ""}
          </Typography>
        </div>
      ),
    },
    {
      title: "Doctor's Comment",
      dataIndex: "comments",
      key: "comments",
      width: 200,
      ...(isMobile ? {} : { fixed: "left" }),
      align: "center",
      render: (v) => v ?? "-",
    },
    {
      title: "Timings",
      key: "timings",
      width: timingsColWidth,
      align: "left",
      onCell: () => ({ style: { overflow: "hidden" } }),
      render: (_, record) => {
        const blocks = renderNurseTimingEntries(record);
        if (blocks) {
          return <div className="nurse-med-timings-stat-cell">{blocks}</div>;
        }
        return <div className="nurse-med-timings-stat-text">-</div>;
      },
    },
  ];

  return (
    <Table
      size="small"
      bordered
      pagination={false}
      dataSource={statData}
      columns={columns}
      rowKey={(r, idx) => r?.id ?? `${r?.name}-${idx}`}
      className="stat-sos-summary-table"
      style={{ width: "100%" }}
      tableLayout="fixed"
      scroll={{ x: 340 + 200 + timingsColWidth }}
    />
  );
};

export default StatMedicines;
